import { FormEvent, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { BuilderCard, BuilderDateField, BuilderTextField, CardHeading, PrimaryButton } from "./builderUi";
import { OrderSearchQuery } from "../../types/evidenceBankCase";
import { AMOUNT } from "../../utils/bankCaseUtils";

type SearchBy = OrderSearchQuery["by"];
type Field = "cardLast4" | "cardBin" | "authCode" | "amount" | "paidOn" | "orderNumber";

// Mirrors the rules in apps/api/modules/evidence/schemas/evidenceBankCaseSchemas.ts.
const LAST4 = /^\d{4}$/;
const BIN = /^\d{6}$/;
const AUTH_CODE = /^[A-Za-z0-9]{1,32}$/;

const SEARCH_BY: { value: SearchBy; label: string }[] = [{ value: "payment", label: "By payment" }, { value: "order", label: "By order ID" }];

export default function OrderSearchCard({ searching, onSearch }: {
  searching: boolean;
  onSearch: (query: OrderSearchQuery) => void;
}) {
  const [by, setBy] = useState<SearchBy>("payment");
  const [cardLast4, setCardLast4] = useState("");
  const [cardBin, setCardBin] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [amount, setAmount] = useState("");
  const [paidOn, setPaidOn] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  // Shown once the agent has tried to search, not while they are still typing.
  const [tried, setTried] = useState(false);

  const typed = { cardLast4: cardLast4.trim(), cardBin: cardBin.trim(), authCode: authCode.trim(), amount: amount.trim(), orderNumber: orderNumber.trim() };
  const errors: Partial<Record<Field, string>> = by === "payment"
    ? {
      cardLast4: typed.cardLast4 && !LAST4.test(typed.cardLast4) ? "Enter the last 4 digits of the card" : "",
      cardBin: typed.cardBin && !BIN.test(typed.cardBin) ? "Enter the first 6 digits of the card" : "",
      authCode: typed.authCode && !AUTH_CODE.test(typed.authCode) ? "An auth code is letters and digits" : "",
      amount: AMOUNT.test(typed.amount) && Number(typed.amount) > 0 ? "" : "Enter the amount as 59.99",
      paidOn: paidOn ? "" : "Enter the date",
    }
    : { orderNumber: typed.orderNumber ? "" : "Enter the order number" };
  const invalid = Object.values(errors).some(Boolean);
  const shown = (message: string | undefined) => (tried && message ? message : undefined);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    if (invalid) return;
    onSearch(by === "payment"
      ? {
        by, amount: typed.amount, paidOn,
        ...(typed.cardLast4 ? { cardLast4: typed.cardLast4 } : {}),
        ...(typed.cardBin ? { cardBin: typed.cardBin } : {}),
        ...(typed.authCode ? { authCode: typed.authCode } : {}),
      }
      : { by, orderNumber: typed.orderNumber });
  };

  return (
    <Box component="form" onSubmit={submit} noValidate>
      <BuilderCard sx={{ gap: "14px" }}>
        <CardHeading>Find the order</CardHeading>

        <Box role="group" aria-label="How to find the order" sx={{ display: "flex", bgcolor: "#f2f4f7", borderRadius: "8px", p: "3px", gap: "3px" }}>
          {SEARCH_BY.map(({ value, label }) => (
            <Box
              key={value}
              component="button"
              type="button"
              aria-pressed={by === value}
              onClick={() => { setBy(value); setTried(false); }}
              sx={{
                flexGrow: 1, flexBasis: 0, height: 38, border: 0, borderRadius: "6px", font: "inherit", cursor: "pointer",
                ...(by === value
                  ? { bgcolor: "#ffffff", fontWeight: 600, color: "#1d2939", boxShadow: "0 1px 2px rgba(16,24,40,0.12)" }
                  : { bgcolor: "transparent", color: "#475467" }),
              }}
            >
              {label}
            </Box>
          ))}
        </Box>

        {by === "payment" ? (
          <>
            <Box sx={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
              <BuilderTextField label="Card last 4" value={cardLast4} onChange={(event) => setCardLast4(event.target.value)} error={Boolean(shown(errors.cardLast4))} helperText={shown(errors.cardLast4)} slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 4 } }} />
              <BuilderTextField label="Card first 6 (optional)" value={cardBin} onChange={(event) => setCardBin(event.target.value)} error={Boolean(shown(errors.cardBin))} helperText={shown(errors.cardBin)} slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 6 } }} />
              <BuilderTextField label="Amount" value={amount} onChange={(event) => setAmount(event.target.value)} error={Boolean(shown(errors.amount))} helperText={shown(errors.amount)} slotProps={{ htmlInput: { inputMode: "decimal" } }} />
              <BuilderDateField label="Payment date" value={paidOn} onChange={setPaidOn} error={Boolean(shown(errors.paidOn))} helperText={shown(errors.paidOn)} />
              <Box sx={{ gridColumn: "1 / -1" }}>
                <BuilderTextField label="Auth code (optional)" value={authCode} onChange={(event) => setAuthCode(event.target.value)} error={Boolean(shown(errors.authCode))} helperText={shown(errors.authCode)} />
              </Box>
            </Box>
            {!typed.cardLast4 && !typed.authCode && (
              <Box sx={{ fontSize: 12, color: "#667085" }}>
                Without the last 4 or an auth code only Shopify stores are searched, on the amount and the date.
              </Box>
            )}
          </>
        ) : (
          <BuilderTextField
            label="Shopify or CheckoutChamp order number"
            value={orderNumber}
            onChange={(event) => setOrderNumber(event.target.value)}
            error={Boolean(shown(errors.orderNumber))}
            helperText={shown(errors.orderNumber)}
          />
        )}

        <PrimaryButton type="submit" fullWidth disabled={searching} startIcon={searching ? <CircularProgress size={16} color="inherit" /> : null}>
          Find order
        </PrimaryButton>
      </BuilderCard>
    </Box>
  );
}
